import { Endpoints } from '@/constants/endpoints';
import { routing } from '@/i18n/routing';
import { PartnersResponse } from '@/types/partners';
import { SiteSettingKey, SiteSettings } from '@/types/site-settings';
import { SlidersResponse } from '@/types/slider';
import { SocialLinksResponse } from '@/types/social';
import { UsefulLinksResponse } from '@/types/useful-link';

const getCurrentLocale = (): string => {
    if (typeof window !== 'undefined') {
        const pathSegments = window.location.pathname.split('/');
        if (pathSegments.length > 1 && routing.locales.includes(pathSegments[1] as any)) {
            return pathSegments[1];
        }
    }
    return routing.defaultLocale;
};

export const createFetchOptions = (): RequestInit => {
    const locale = getCurrentLocale();
    return {
        headers: {
            'Content-Type': 'application/json',
            'Accept-Language': locale,
        },
    };
};

export const getSliders = async (): Promise<SlidersResponse> => {
    try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}${Endpoints.sliders}`, {
            ...createFetchOptions(),
            next: { revalidate: 0 },
        });
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching sliders:', error);
        return [];
    }
};

export const getSocialLinks = async (): Promise<SocialLinksResponse> => {
    try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}${Endpoints.socialLinks}`, {
            ...createFetchOptions(),
            cache: 'no-cache',
            next: { revalidate: 0 },
        });
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching social links:', error);
        return [];
    }
};

export const subscribeNewsletter = async (email: string) => {
    try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}${Endpoints.subscribeNewsletter}`, {
            ...createFetchOptions(),
            method: 'POST',
            body: JSON.stringify({ email }),
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
        }

        return await response.json();
    } catch {
        return { error: 'Failed to subscribe to newsletter' };
    }
};

export const getUsefulLinks = async (): Promise<UsefulLinksResponse> => {
    try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}${Endpoints.usefulLinks}`, {
            ...createFetchOptions(),
            next: { revalidate: 0 },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching useful links:', error);
        return [];
    }
};

export const getPartners = async (): Promise<PartnersResponse> => {
    try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}${Endpoints.partners}`, {
            ...createFetchOptions(),
            next: { revalidate: 0 },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching partners:', error);
        return [];
    }
};

export const getSiteSettings = async (): Promise<SiteSettings> => {
    try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}${Endpoints.settings}`, {
            ...createFetchOptions(),
            next: { revalidate: 0 },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        const settings: SiteSettings = {
            header_logo: '',
            footer_logo: '',
            address: '',
            email: '',
        };

        for (const item of data) {
            settings[item.key as SiteSettingKey] = item.value;
        }

        return settings;
    } catch (error) {
        console.error('Error fetching settings:', error);
        return {
            header_logo: '/svg/logo-light.svg',
            footer_logo: '/svg/logo-light.svg',
            address: '',
            email: '',
        };
    }
};
